/**
 * Pure logic for the wallboard's Expected daemons tile (WI 34278). Each Control
 * Center compares its host's entry in `lib/expected-daemons.json` with the
 * launchd / scheduled-task units actually loaded, and publishes the missing ones
 * as `daemons` on its hostMetrics sample. This turns those per-host reports into
 * tile rows.
 */
import { parseUtcMs } from '../models/flock-telemetry.utils';
import type { HostDaemons } from './host-metric.models';

type MissingDaemon = HostDaemons['missing'][number];
import { normalizeHost } from './machines.logic';

/**
 * The CC enumerates units once a minute. A report older than this means the
 * publisher stopped (or runs a CC without the daemon tick), so its view is shown
 * as stale and never counts toward "all running".
 */
export const DAEMON_STALE_MS = 3 * 60_000;

export interface HostDaemonReport {
  hostId: string;
  hostname: string;
  /** Null when the host does not report daemons (not the same as none missing). */
  daemons: HostDaemons | null | undefined;
}

export interface DaemonRow {
  key: string;
  host: string;
  /** Label without the com.ryn. prefix, for the wall screen. */
  label: string;
  fullLabel: string;
  /**
   * The CC's reason code (not-installed, down, failing, no-pid, exited, or the
   * catalog-drift codes uncatalogued / host-not-catalogued).
   */
  reason: string | null;
  reasonText: string;
  stale: boolean;
}

export interface DaemonSummary {
  rows: DaemonRow[];
  /** Machines with a daemons report (fresh or stale). */
  reportingHosts: number;
  /** Machines whose report is inside DAEMON_STALE_MS. */
  freshHosts: number;
  staleHosts: string[];
  /** Units expected across the fresh machines. */
  expectedTotal: number;
}

const REASON_TEXT: Record<string, string> = {
  'not-installed': 'not installed',
  down: 'not loaded',
  failing: 'not running',
  'no-pid': 'not running (no pid)',
  // WI 34333: the CC's manifest drifted from Ryn_Core's daemon-catalog.json.
  uncatalogued: 'missing from daemon-catalog.json',
  'host-not-catalogued': 'not catalogued for this host',
};

/** Manifest-vs-catalog drift (WI 34333): a config fault, not a dead unit. */
export const CATALOG_DRIFT_REASONS: ReadonlySet<string> = new Set([
  'uncatalogued',
  'host-not-catalogued',
]);

function reasonText(m: MissingDaemon): string {
  if (m.reason === 'exited')
    return m.lastExit != null
      ? `last run exited ${m.lastExit}`
      : 'last run failed';
  return (m.reason && REASON_TEXT[m.reason]) || 'not running';
}

function shortLabel(label: string): string {
  return label.replace(/^com\.ryn(ai)?\./, '');
}

export function buildDaemonSummary(
  hosts: HostDaemonReport[],
  nowMs: number
): DaemonSummary {
  // One machine can publish under two host ids ("rynais-Mac-Studio" and
  // "rynais-mac-studio"): keep its newest report.
  const newest = new Map<string, { checkedMs: number; daemons: HostDaemons }>();
  for (const h of hosts) {
    if (!h.daemons) continue;
    const key = normalizeHost(h.hostname) || normalizeHost(h.hostId);
    if (!key) continue;
    const checkedMs = parseUtcMs(h.daemons.checkedAt) ?? 0;
    const prev = newest.get(key);
    if (!prev || checkedMs > prev.checkedMs)
      newest.set(key, { checkedMs, daemons: h.daemons });
  }

  const rows: DaemonRow[] = [];
  const staleHosts: string[] = [];
  let freshHosts = 0;
  let expectedTotal = 0;
  for (const [host, { checkedMs, daemons }] of newest) {
    const stale = nowMs - checkedMs > DAEMON_STALE_MS;
    if (stale) staleHosts.push(host);
    else {
      freshHosts++;
      expectedTotal += daemons.expected;
    }
    for (const m of daemons.missing) {
      rows.push({
        key: `${host}|${m.label}`,
        host,
        label: shortLabel(m.label),
        fullLabel: m.label,
        reason: m.reason,
        reasonText: reasonText(m),
        stale,
      });
    }
  }
  rows.sort(
    (a, b) =>
      Number(a.stale) - Number(b.stale) ||
      a.host.localeCompare(b.host) ||
      a.label.localeCompare(b.label)
  );
  staleHosts.sort();
  return {
    rows,
    reportingHosts: newest.size,
    freshHosts,
    staleHosts,
    expectedTotal,
  };
}
